/**
 * Plain-text diagnostics report, meant to be PASTED as is.
 *
 * It comes from a concrete case: the app stayed "syncing" forever and the
 * reason was that the PowerSync service was not even up. From the
 * screen there was no way to tell "it does not connect" from "it connects and is slow", and
 * describing it by hand loses half the data.
 *
 * SINGLE SOURCE. The first version took a hand-rolled object while the screen
 * painted its rows on its own: two sources for the same thing, so adding a
 * row to the UI left the report incomplete without anyone noticing. Now
 * both consume the SAME array of sections: the screen renders it, this
 * function serializes it and the hint is computed by reading it.
 *
 * Every item carries three fields for three different reasons:
 *   · `label`/`value` — already-translated text: what you see and what you copy.
 *   · `status`        — only used by the UI for the color; the report ignores it.
 *   · `raw`           — UNtranslated value, so that the logic does not depend on the
 *                       language. Without it the hint would stop firing as soon as
 *                       someone changed a translation.
 *
 * Does NOT include personal data: no email, no name, no token. The caller constructing
 * sections is responsible for trimming identifiers.
 */

export type DiagnosticStatus = "success" | "warning" | "danger" | "neutral";

export interface DiagnosticItem {
  /** Stable identifier, independent of the language. The logic uses it. */
  key: string;
  /** Already-translated label. */
  label: string;
  /** Already-translated / formatted value, as seen on screen. */
  value: string;
  /** UI only. */
  status?: DiagnosticStatus;
  /** Raw value for logic that must not depend on the language. */
  raw?: string | number | boolean | null;
}

export interface DiagnosticSection {
  title: string;
  items: DiagnosticItem[];
}

export interface DiagnosticsReportInput {
  appName: string;
  /** ISO timestamp. It is passed in; the clock is not read here (pure function). */
  timestamp: string;
  sections: DiagnosticSection[];
}

/** Looks up by `key`, never by label: labels are translated. */
function search(sections: DiagnosticSection[], key: string): DiagnosticItem | undefined {
  for (const s of sections) {
    const found = s.items.find((i) => i.key === key);
    if (found) {
      return found;
    }
  }
  return undefined;
}

/**
 * Actionable hint for the pattern that originated all of this: there is a session and the
 * sync does not start. It is almost always the service being down, not the app, and
 * saying so here saves looking in the wrong place.
 */
function hint(sections: DiagnosticSection[]): string[] {
  const session = search(sections, "session");
  const sync = search(sections, "sync");
  if (
    session?.raw !== true ||
    !sync ||
    sync.raw === "connected" ||
    sync.raw === undefined ||
    sync.raw === null
  ) {
    return [];
  }
  return [
    "",
    "## Pista",
    `La sesión está activa pero PowerSync no llega a "connected" (${String(sync.raw)}).`,
    "Comprobar que el servicio de PowerSync responde en la URL de arriba: si da",
    "502 o no responde, el problema es la pila, no la app.",
  ];
}

export function buildDiagnosticsReport(input: DiagnosticsReportInput): string {
  const partes: string[] = [`# Diagnóstico — ${input.appName}`, input.timestamp];

  for (const seccion of input.sections) {
    partes.push("", `## ${seccion.title}`);
    for (const item of seccion.items) {
      // An empty field is MARKED instead of omitted: in a pasted report, an
      // absent datum and one not collected read the same if you do not say which.
      partes.push(`- ${item.label}: ${item.value === "" ? "(sin dato)" : item.value}`);
    }
  }

  partes.push(...hint(input.sections));
  return partes.join("\n");
}
